<template>
    <div
        class="text-[16px] mt-4 font-semibold"
        :class="{ 'text-red-error': hasTextError }"
    >
        {{ $t('ENTER_CHARACTERS', { limit: limit }) }}
    </div>

    <div
        class="leading-tight text-[14px]"
        :class="{ 'text-red-error': hasTextError }"
    >
        {{ $t('CHARACTERS_PREVIEW')}}
    </div>
    <base-input
        :input-name="attribute"
        :input-value="inputValue"
        :input-placeholder="$t('ENTER_INITIALS')"
        :show-error-message="hasTextError"
        :error-message="hasValidationCopy ? '' : $t('REQUIRED_CHARACTERS')"
        class="w-full mt-2"
        :show-character-count="true"
        :input-max-length="limit"
        @input="updateValue"
    />
    <div
        v-if="hasProfanity"
        class="text-red-error text-[14px] mt-1 leading-5"
    >
        {{ $t('PROFANITY_COPY_1') }}
        <a
            :href="$t('TERMS_LINK')"
            class="underline"
            target="_blank"
            rel="noopener noreferrer"
        >
            {{ $t('PROFANITY_COPY_TERMS_LINK') }}
        </a>
        {{ $t('PROFANITY_COPY_2') }}
    </div>
    <div
        v-else-if="hasDisallowedCharacters"
        class="text-red-error text-[14px] mt-1 leading-5"
    >
        {{ $t('DISALLOWED_CHARACTERS_COPY_A') }}
        <a
            :href="$t('DISALLOWED_CHARACTERS_COPY_LINK_URL')"
            class="underline"
            target="_blank"
            rel="noopener noreferrer"
        >
            {{ $t('DISALLOWED_CHARACTERS_COPY_LINK') }}
        </a>
        {{ $t('DISALLOWED_CHARACTERS_COPY_B') }}
    </div>
</template>

<script>
    import BaseInput from './BaseInput.vue';

    import allStores from '../stores/all';

    let uniqueCounter = 1;

    function unique(prefix) {
        uniqueCounter += 1;

        return `${prefix}${uniqueCounter}`;
    }

    export default {
        mixins: [allStores],

        components: {
            BaseInput,
        },

        props: {
            limit: Number,
            characters: String,
            placement: Object,
            attribute: String,
            hasErrorState: Boolean,
            shouldSeparateWithPeriods: {
                type: Boolean,
                default: false,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
        },

        emits: ['handleReset', 'moderationResult', 'update-value', 'validation-error'],

        data() {
            return {
                hasProfanity: false,
                hasDisallowedCharacters: false,
                disallowedDraft: '',
            };
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            inputValue() {
                if (this.hasDisallowedCharacters) {
                    return this.disallowedDraft;
                }

                const component = this.viewController.selectedAtPlacement(this.placement.code);

                if (component) {
                    return component.custom?.[this.attribute] || '';
                }

                return '';
            },

            inErrorState() {
                if (!this.isDirty) {
                    return false;
                }

                return this.customizerStore.panelsInErrorState
                    .includes(`${this.currentGroup}-${this.attribute}`);
            },

            hasTextError() {
                return this.inErrorState || this.hasValidationCopy;
            },

            hasValidationCopy() {
                return this.hasProfanity || this.hasDisallowedCharacters;
            },

            currentGroup() {
                return this.customizerStore.currentGroup;
            },
        },

        watch: {
            hasValidationCopy(hasError) {
                this.$emit('validation-error', hasError);
            },

            async isDirty(isDirty) {
                await this.$nextTick();
                if (isDirty && this.inputValue.length === 0) {
                    this.customizerStore.addPanelToErrorState(`${this.currentGroup}-${this.attribute}`);
                }
            },
        },

        methods: {
            clearInput() {
                this.disallowedDraft = '';
                this.hasDisallowedCharacters = false;

                this.viewController.commitCustomAttributes(this.placement.code, {
                    [this.attribute]: '',
                });
            },

            checkText(value) {
                if (this.hasDisallowedCharacters) {
                    return;
                }

                const moderationRequest = unique('moderate-');

                this.lastModerationRequest = moderationRequest;

                this.viewController.reviewText(value).then((result) => {
                    if (moderationRequest === this.lastModerationRequest) {
                        if (result.hasProfanity) {
                            this.hasProfanity = true;

                            this.customizerStore.addPanelToErrorState(`${this.currentGroup}-${this.attribute}`);

                            this.approved = false;
                        } else {
                            this.$emit('moderationResult', true);

                            this.applyValue(value);

                            this.approved = true;
                        }
                    }
                });
            },

            applyValue(value) {
                const normalizedValue = value.trim();
                const nextValue = normalizedValue;

                // if (this.shouldSeparateWithPeriods && normalizedValue.length > 0) {
                //     nextValue = `${normalizedValue.split('').join('.')}.`;
                // }

                this.viewController.commitCustomAttributes(this.placement.code, {
                    [this.attribute]: nextValue,
                });

                this.hasProfanity = false;
                this.hasDisallowedCharacters = false;
                this.disallowedDraft = '';

                this.customizerStore.removePanelFromErrorState(`${this.currentGroup}-${this.attribute}`);

                this.$emit('update-value', {
                    placementCode: this.placement.code,
                    attribute: this.attribute,
                    value: nextValue,
                });
            },

            containsDisallowedCharacters(value) {
                if (!this.characters || this.characters.length === 0) {
                    return false;
                }

                return value.split('').some((character) => !this.characters.includes(character));
            },

            async updateValue(from) {
                let value = from.target.value ?? '';

                if (value.trim().length === 0) {
                    this.hasProfanity = false;
                    this.hasDisallowedCharacters = false;
                    this.disallowedDraft = '';
                    this.lastModerationRequest = null;

                    if (this.isDirty) {
                        this.customizerStore.addPanelToErrorState(`${this.currentGroup}-${this.attribute}`);
                    }

                    this.viewController.commitCustomAttributes(this.placement.code, {
                        [this.attribute]: '',
                    });

                    if (from.target) {
                        from.target.value = '';
                    }

                    return;
                }

                value = value.toUpperCase().trimStart();

                if (value.length > this.limit) {
                    value = value.substring(0, this.limit);
                }

                if (from.target && from.target.value !== value) {
                    from.target.value = value;
                }

                if (this.containsDisallowedCharacters(value)) {
                    this.hasProfanity = false;
                    this.hasDisallowedCharacters = true;
                    this.disallowedDraft = value;
                    this.customizerStore.addPanelToErrorState(`${this.currentGroup}-${this.attribute}`);
                    return;
                }

                this.hasDisallowedCharacters = false;
                this.disallowedDraft = '';

                this.checkText(value);
            },
        },
    };
</script>
